
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * SocialTradingOrderInvestor struct
 */
class SocialTradingOrderInvestor {
  /**
   * Initialize struct
   * @param {!number=} masterOrderId
   * @param {!number=} masterLogin
   * @constructor
   */
  constructor (masterOrderId = new UInt64(0, 0), masterLogin = new UInt64(0, 0)) {
    this.MasterOrderId = masterOrderId
    this.MasterLogin = masterLogin
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SocialTradingOrderInvestor}
   * @param {!SocialTradingOrderInvestor} other Other struct
   * @returns {!SocialTradingOrderInvestor} This struct
   */
  copy (other) {
    if (other.MasterOrderId != null) {
      this.MasterOrderId = UInt64.fromNumber(other.MasterOrderId)
    } else {
      this.MasterOrderId = null
    }
    if (other.MasterLogin != null) {
      this.MasterLogin = UInt64.fromNumber(other.MasterLogin)
    } else {
      this.MasterLogin = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SocialTradingOrderInvestor}
   * @returns {!SocialTradingOrderInvestor} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SocialTradingOrderInvestorModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SocialTradingOrderInvestorModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SocialTradingOrderInvestor}
   * @param {!SocialTradingOrderInvestor} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SocialTradingOrderInvestor)) {
      throw new TypeError('Instance of SocialTradingOrderInvestor is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SocialTradingOrderInvestor}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      MasterOrderId: ((this.MasterOrderId != null) ? this.MasterOrderId.toNumber() : null),
      MasterLogin: ((this.MasterLogin != null) ? this.MasterLogin.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SocialTradingOrderInvestor.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SocialTradingOrderInvestor} other Object value
   * @returns {!SocialTradingOrderInvestor} Created struct
   */
  static fromObject (other) {
    return new SocialTradingOrderInvestor().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SocialTradingOrderInvestor}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SocialTradingOrderInvestor.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SocialTradingOrderInvestor}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 203
  }
}

export { SocialTradingOrderInvestor };

/**
 * Fast Binary Encoding SocialTradingOrderInvestor field model
 */
class FieldModelSocialTradingOrderInvestor extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.MasterOrderId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.MasterLogin = new fbe.FieldModelUInt64(buffer, this.MasterOrderId.fbeOffset + this.MasterOrderId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.MasterOrderId.fbeSize + this.MasterLogin.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.MasterOrderId.fbeExtra + this.MasterLogin.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSocialTradingOrderInvestor.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 203
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MasterOrderId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MasterOrderId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MasterOrderId.fbeSize

    if ((fbeCurrentSize + this.MasterLogin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MasterLogin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MasterLogin.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!SocialTradingOrderInvestor} fbeValue Default value, defaults is new SocialTradingOrderInvestor()
   * @returns {!SocialTradingOrderInvestor} SocialTradingOrderInvestor value
   */
  get (fbeValue = new SocialTradingOrderInvestor()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!SocialTradingOrderInvestor} fbeValue SocialTradingOrderInvestor value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MasterOrderId.fbeSize) <= fbeStructSize) {
      fbeValue.MasterOrderId = this.MasterOrderId.get()
    } else {
      fbeValue.MasterOrderId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MasterOrderId.fbeSize

    if ((fbeCurrentSize + this.MasterLogin.fbeSize) <= fbeStructSize) {
      fbeValue.MasterLogin = this.MasterLogin.get()
    } else {
      fbeValue.MasterLogin = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MasterLogin.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {!SocialTradingOrderInvestor} fbeValue SocialTradingOrderInvestor value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSocialTradingOrderInvestor}
   * @param {SocialTradingOrderInvestor} fbeValue SocialTradingOrderInvestor value
   */
  setFields (fbeValue) {
    this.MasterOrderId.set(fbeValue.MasterOrderId)
    this.MasterLogin.set(fbeValue.MasterLogin)
  }
}

export { FieldModelSocialTradingOrderInvestor };

/**
 * Fast Binary Encoding SocialTradingOrderInvestor model
 */
class SocialTradingOrderInvestorModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSocialTradingOrderInvestor(this.buffer, 4)
  }

  /**
   * Get the SocialTradingOrderInvestor model
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!FieldModelSocialTradingOrderInvestor} model SocialTradingOrderInvestor model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SocialTradingOrderInvestorModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSocialTradingOrderInvestor.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SocialTradingOrderInvestorModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SocialTradingOrderInvestorModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SocialTradingOrderInvestorModel}
   * @param {!SocialTradingOrderInvestor} value SocialTradingOrderInvestor value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SocialTradingOrderInvestorModel}
   * @param {!SocialTradingOrderInvestor} value SocialTradingOrderInvestor value, defaults is new SocialTradingOrderInvestor()
   * @return {!object} Deserialized SocialTradingOrderInvestor value and its size
   */
  deserialize (value = new SocialTradingOrderInvestor()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SocialTradingOrderInvestor(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SocialTradingOrderInvestor(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SocialTradingOrderInvestorModel}
   * @param {!number} prev Previous SocialTradingOrderInvestor model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SocialTradingOrderInvestorModel };
